import { useState } from 'react'
import { Button, Col, Flex, Form, Row, Typography, notification } from 'antd'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { formatDataBeforeSubmit, generateValidationSchema } from '../components/utils'
import '../components/index.scss'
import FormPayee from '../components/formPayee'
import { addNewPayeeMatchParameters } from 'src/app/shared/services/PayeeMatchParameters'
import { IPayloadPayeeMatchParameters } from 'src/app/shared/models/payeeMatchParameters.model'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { yupResolver } from '@hookform/resolvers/yup'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { useWindowSize } from 'src/app/shared/hooks/useWindowSize'

const AddNewPayeeMatchParameters = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { mode } = useWindowSize()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingSaveMore, setIsLoadingSaveMore] = useState(false)
  const [isSaveMore, setIsSavaMore] = useState<boolean>(false)
  const [api, contextHolder] = notification.useNotification()

  const defaultValues: IPayloadPayeeMatchParameters = {
    profileName: '',
    createExceptionsForConfidenceScoresBelowDefault: undefined,
    // detectAdditionalNames: false,
    // enterAdditionalNames: undefined,
  }

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    setError,
    setValue,
  } = useForm<any>({ mode: 'all', defaultValues, resolver: yupResolver(generateValidationSchema(t)) })
  const handleCancel = () => {
    navigate(menuUrls.payeeMatchParameters)
  }
  const profileName = useWatch({
    control,
    name: 'profileName',
  })

  const addPayeeMatchParameters = async (data: IPayloadPayeeMatchParameters) => {
    if (isSaveMore) {
      setIsLoadingSaveMore(true)
    } else {
      setIsLoading(true)
    }

    try {
      const res = await addNewPayeeMatchParameters(formatDataBeforeSubmit(data))
      const { statusCode } = res

      // No Duplicate
      if (statusCode === 'Success') {
        setIsLoading(false)

        if (isSaveMore) {
          api.success({
            message: (
              <RichTextField
                richText={t('SystemMessages:' + SYSTEM_MESSAGE_IDS.SAVE_NEW_SUCCESSFULLY, {
                  Object: data?.profileName,
                })}
              />
            ),
          })
          setValue('profileName', '')
          setValue(
            'createExceptionsForConfidenceScoresBelowDefault',
            data?.createExceptionsForConfidenceScoresBelowDefault,
          )
          setIsSavaMore(false)
          setIsLoadingSaveMore(false)
          return
        }
        navigate(menuUrls.payeeMatchParameters, {
          state: {
            payeeMatchParameters: {
              type: 'success',
              message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.SAVE_NEW_SUCCESSFULLY, {
                Object: data?.profileName,
              }),
            },
          },
        })
      }
    } catch (error: any) {
      if (error?.statusCode === 'PPA06') {
        setError('profileName', {
          message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.OBJECT_DUPLICATED, {
            Object: data?.profileName,
          }),
        })
        setIsLoading(false)
        setIsLoadingSaveMore(false)
      }
    }
  }

  const isSaveButtonDisabled = !!Object.keys(errors)?.length || !isValid || !profileName

  const [form] = Form.useForm()

  return (
    <>
      {contextHolder}
      <Form
        form={form}
        name='payee-match-parameter-form'
        className='payee-match-parameter-form-container'
        layout='vertical'
        onFinish={handleSubmit(addPayeeMatchParameters)}
      >
        <FormPayee control={control} errors={errors} isViewOnly={false} setValue={setValue} />

        <Flex className='button-group' justify='flex-end' gap={24}>
          <Row gutter={24} style={{ width: ['tablet', 'mobile'].includes(mode) ? '100%' : '600px' }}>
            <Col span={24} md={8} style={{ paddingBottom: mode === 'mobile' ? 24 : 0 }}>
              <Button className='cancel-button' onClick={handleCancel} style={{ width: '100%' }}>
                {t('OverallFunctionality:cancel_btn')}
              </Button>
            </Col>
            <Col span={12} md={8}>
              <Button
                className='save-and-add-more-button'
                onClick={() => {
                  setIsSavaMore(true)
                  form.submit()
                }}
                disabled={isSaveButtonDisabled}
                htmlType='button'
                loading={isLoadingSaveMore}
                style={{ width: '100%' }}
              >
                <Typography.Text
                  style={{ paddingRight: '5px' }}
                  className={isSaveButtonDisabled ? 'button-save-more-disable' : 'button-save-more'}
                >
                  {t('PayeeMatchParameters:addNewPayeeMatchParameters.button.saveAndAddMore')}
                </Typography.Text>
              </Button>
            </Col>
            <Col span={12} md={8}>
              <Button
                className='save-button'
                type='primary'
                disabled={isSaveButtonDisabled}
                htmlType='button'
                onClick={form.submit}
                loading={isLoading}
                style={{ width: '100%' }}
              >
                {t('OverallFunctionality:save_changes_btn')}
              </Button>
            </Col>
          </Row>
        </Flex>
      </Form>
    </>
  )
}

export default AddNewPayeeMatchParameters
